import React, { useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { ConditionRow } from '../../../../../../../components/ConditionRow/ConditionRow'
import { FilterOperator } from '../../../../../../../models/Account'
import { FormFieldDefinition, IntegrationObjectRelationship } from '../../../hooks'
import { useAppSelector } from '../../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useGetDescribeQuery } from '../../../../../../../store/integrations/integrations'
import { IntegrationServiceId } from '../../../../../../../common/integrations'
import { FilterCondition } from './types'

export interface ConditionRowContainerProps {
  serviceId: IntegrationServiceId
  condition: FilterCondition
  availableFormFieldDefinitions: FormFieldDefinition[]
  availableRelationships?: IntegrationObjectRelationship[]
  operatorOptions: SelectOption[]
  loading?: boolean
  onChange: (patch: Partial<FilterCondition>) => void
  onRemove: () => void
}

export const ConditionRowContainer: React.FC<ConditionRowContainerProps> = ({
  serviceId,
  condition,
  availableFormFieldDefinitions,
  availableRelationships,
  operatorOptions,
  loading,
  onChange,
  onRemove,
}) => {
  const [referenceObject, setReferenceObject] = useState<IntegrationObjectRelationship | null>(null)
  const accountId = useAppSelector(selectAccountId)

  const { data: describe, isFetching: isDescribeFetching } = useGetDescribeQuery(
    {
      accountId,
      connector: serviceId,
      context: { objectName: referenceObject?.referenceTo || '' },
    },
    { skip: !referenceObject || !accountId },
  )

  const referenceObjectFormFieldDefinitions: FormFieldDefinition[] = useMemo(() => describe?.data?.describe?.formFieldDefinitions || [], [describe])

  const filterOptions: SelectOption[] = useMemo(() => {
    return [
      ...availableFormFieldDefinitions.map((field) => ({ id: field.name, label: `${field.label} (${field.name})` })),
      ...(availableRelationships ? availableRelationships.map((rel) => ({ id: rel.relationshipName, label: rel.label })) : []),
      ...referenceObjectFormFieldDefinitions.map((field) => ({
        id: `${referenceObject?.relationshipName}.${field.name}`,
        label: `${referenceObject?.relationshipName}.${field.label} (${referenceObject?.relationshipName}.${field.name})`,
      })),
    ]
  }, [availableFormFieldDefinitions, availableRelationships, referenceObjectFormFieldDefinitions, referenceObject])

  const resolveFieldDefinition = (id: string): FormFieldDefinition | undefined => {
    return (
      availableFormFieldDefinitions.find((field) => field.name === id) ??
      referenceObjectFormFieldDefinitions.find((field) => `${referenceObject?.relationshipName}.${field.name}` === id)
    )
  }

  const handleLeftValueChange = (id: string) => {
    const relationship = availableRelationships?.find((rel) => rel.relationshipName === id)
    if (relationship) {
      setReferenceObject(relationship)
      onChange({ reference: null, value: '', valueType: null, valueFormat: null })
    } else {
      const fieldDefinition = resolveFieldDefinition(id)
      onChange({
        reference: id,
        value: '',
        valueType: fieldDefinition?.type ?? null,
        valueFormat: fieldDefinition?.extraTypeInfo ?? null,
      })
    }
  }

  return (
    <ConditionRow
      selectedLeftValue={condition.reference}
      selectedOperator={condition.operator}
      leftValueOptions={filterOptions}
      operatorOptions={operatorOptions}
      leftValuePlaceholder='Select field'
      operatorPlaceholder='Select operator'
      loading={loading || isDescribeFetching}
      rightValueComponent={
        <Form.Control
          type='text'
          className='p-2 zocks-bg-color-gray-100 rounded'
          placeholder='Add value'
          value={condition.value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ value: e.target.value })}
          disabled={condition.enforced}
        />
      }
      onLeftValueChange={handleLeftValueChange}
      onOperatorChange={(id) => onChange({ operator: id as FilterOperator })}
      onRemove={onRemove}
    />
  )
}
